import ApiIcon from '@mui/icons-material/Api';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CircleIcon from '@mui/icons-material/Circle';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { ProductGroups, Products, Routes } from 'enums';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiRoundTextField from 'features/ui/base-components/pai-round-text-field';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { License } from '../../../types/licenses.types';
import useMapProductNames from './licenses/use-map-product-names';

const LicenseItemSkeleton: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.licenseItem}>
      <Stack className={classes.licenseItemHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="text" width={120} height={30} />
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: '6px' }} />
          <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: '6px' }} />
          <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: '6px' }} />
        </Stack>
      </Stack>
      <Stack className={classes.licenseItemContent} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={60} height={21} />
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={200} height={21} />
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={200} height={21} />
        </Stack>
      </Stack>
    </div>
  );
};

const getProductGroup = (productName: string): ProductGroups | undefined => {
  switch (productName) {
    case Products.SAIA_RECRUITING:
      return ProductGroups.HR_BOOSTER;
    case Products.SAIA_SALES:
      return ProductGroups.LEAD_BOOSTER;
    case Products.MOTIVE:
    case Products.LEAD:
    default:
      return undefined;
  }
};

interface LicenseItemProps {
  license: License;

  mapProductName(product: string): string;

  handleOpenManageLicenseUsersDialog(license: License): () => void;

  handleManageSubscription(): () => void;

  handleOpenCancelLicenseDialog(licenseId: string): () => void;

  /** Opens the cancel dialog (results, reason, offer) before the Stripe portal. */
  handleOpenCancelSubscription?(): () => void;
}

const LicenseItem: React.FC<LicenseItemProps> = ({
  license,
  mapProductName,
  handleManageSubscription,
  handleOpenCancelSubscription,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const hrBoosterLogo = '/dashboard/images/logos/hr_booster_logo_short.svg';
  const leadBoosterLogo = '/dashboard/images/logos/lead_booster_logo_short.svg';
  const paiLogo = '/dashboard/images/logos/product_logo_white.svg';

  const productGroup = getProductGroup(license.product);
  const logo = productGroup === ProductGroups.HR_BOOSTER ? hrBoosterLogo : productGroup === ProductGroups.LEAD_BOOSTER ? leadBoosterLogo : paiLogo;

  const {
    product,
    isExpired,
    isActive,
    expiresAt,
    credits,
    creditsUsed,
  } = license;

  const isStripeLicense = !!license.stripeSubscriptionId;

  const getUsageDescription = () => {
    switch (product) {
      case Products.SAIA_RECRUITING:
      case Products.SAIA_SALES:
        return null;
      case Products.MOTIVE:
      case Products.LEAD: {
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <ApiIcon className={classes.icon} />
            <Typography className={classes.secondaryText}>{`${creditsUsed ?? 0}/${credits ?? 0} ${t('requests')}`}</Typography>
          </Stack>
        );
      }
      default:
        return null;
    }
  };

  const getLicenseStatus = () => {
    if (isExpired) {
      return (
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <CircleIcon className={cx(classes.statusIcon, 'expired')} />
          <Typography className={classes.secondaryText}>{t('inactive')}</Typography>
        </Stack>
      );
    }
    if (!isActive) {
      return (
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <CircleIcon className={cx(classes.statusIcon, 'canceled')} />
          <Typography className={classes.secondaryText}>{t('canceled')}</Typography>
        </Stack>
      );
    }
    return (
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <CircleIcon className={cx(classes.statusIcon, 'active')} />
        <Typography className={classes.secondaryText}>{t('active')}</Typography>
      </Stack>
    );
  };

  const getLicenseExpirationDate = () => {
    if (!expiresAt) {
      return null;
    }
    if (isExpired) {
      return `${t('expiredAt')}: ${formatDateTime(new Date(expiresAt).toISOString(), 'P')} ${!isStripeLicense ? `(${t('bookedManually')})` : ''}`;
    }
    if (!isActive) {
      return `${t('expiresAt')}: ${formatDateTime(new Date(expiresAt).toISOString(), 'P')}`;
    }
    if (!isStripeLicense) {
      return `${t('expiresAt')}: ${formatDateTime(new Date(expiresAt).toISOString(), 'P')} (${t('bookedManually')})`;
    }
    return `${t('nextPayment')}: ${formatDateTime(new Date(expiresAt).toISOString(), 'P')}`;
  };

  return (
    <div className={classes.licenseItem}>
      <Stack className={classes.licenseItemHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <img src={logo} alt="logo" className={classes.logo} />
          <Typography className={classes.licenseName}>{mapProductName(product)}</Typography>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          {/* „Benutzer verwalten" entfernt: Lizenzen gelten für den ganzen Workspace und werden beim
              Verbinden an ein LinkedIn-Konto gebunden — die Zuweisung an Benutzer speicherte nichts. */}
          {isStripeLicense && (
            <PaiButton text={t('manageSubscriptionAndInvoices')} size={'extra-small'} border={'rounded'} action={handleManageSubscription()} />
          )}
          {/* Kündigen: erst Bilanz + Grund + Angebot (Engagement Center), dann weiter ins Stripe-Kundenportal. */}
          {isStripeLicense && isActive && handleOpenCancelSubscription ? (
            <Button size={'small'} variant={'outlined'} sx={{ textTransform: 'none', borderRadius: '6px' }} onClick={handleOpenCancelSubscription()}>
              {t('engCancelButton')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      <Stack className={classes.licenseItemContent} gap={2}>
        <div>{getLicenseStatus()}</div>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <CalendarMonthIcon className={classes.icon} />
          <Typography className={classes.secondaryText}>{getLicenseExpirationDate()}</Typography>
        </Stack>
        <div>{getUsageDescription()}</div>
      </Stack>
    </div>
  );
};

interface Props {
  licenses: License[];
  total: number;
  loading: boolean;
  itemsPerPage: number;
  page: number;
  searchParam: string;

  handleChangePage(event: unknown, newPage: number): void;

  handleSearchParamChange(event: React.ChangeEvent<HTMLInputElement>): void;

  searchKeyDown(): void;

  searchKeyUp(): void;

  handleClearSearchField(): void;

  handleOpenManageLicenseUsersDialog(license: License): () => void;

  handleManageSubscription(): () => void;

  handleOpenCancelLicenseDialog(licenseId: string): () => void;

  /** Opens the cancel dialog (results, reason, offer) before the Stripe portal. */
  handleOpenCancelSubscription?(): () => void;
}

const ManageLicenses: React.FC<Props> = ({
  licenses,
  total,
  loading,
  itemsPerPage,
  page,
  searchParam,
  handleChangePage,
  handleSearchParamChange,
  searchKeyDown,
  searchKeyUp,
  handleClearSearchField,
  handleOpenManageLicenseUsersDialog,
  handleManageSubscription,
  handleOpenCancelLicenseDialog,
  handleOpenCancelSubscription,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const licensesListRef = useRef<HTMLDivElement>(null);

  const mapProductName = useMapProductNames();
  const navigate = useNavigate();

  useEffect(() => {
    licensesListRef.current?.scrollTo({ top: 0 });
  }, [page]);

  return (
    <div className={classes.manageLicenses}>
      <Stack className={classes.listHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
        <Typography className={classes.blockTitle}>{t('licenseListTitle')}</Typography>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <PaiRoundTextField
            value={searchParam}
            variant={'search'}
            placeholder={t('enterUserNameOrEmailHere')}
            onClear={handleClearSearchField}
            onChange={handleSearchParamChange}
            onKeyDown={searchKeyDown}
            onKeyUp={searchKeyUp}
            style={{ width: '360px' }}
          />
          <PaiButton text={t('buyLicense')} size={'small'} border={'rounded'} action={() => navigate(Routes.UPGRADE)} />
        </Stack>
      </Stack>
      <Stack ref={licensesListRef} className={classes.licenses} gap={2}>
        {loading
          ? Array.from({ length: 10 }).map((_, index) => <LicenseItemSkeleton key={index} />)
          : licenses.map((license) => (
              <LicenseItem
                key={license._id}
                license={license}
                mapProductName={mapProductName}
                handleOpenManageLicenseUsersDialog={handleOpenManageLicenseUsersDialog}
                handleManageSubscription={handleManageSubscription}
                handleOpenCancelLicenseDialog={handleOpenCancelLicenseDialog}
                handleOpenCancelSubscription={handleOpenCancelSubscription}
              />
            ))}
      </Stack>
      <Stack direction={'row'} justifyContent={'flex-end'} style={{ marginTop: 'auto' }}>
        <PaiPagination total={total} itemsPerPage={itemsPerPage} page={page} handleChangePage={handleChangePage} hidden={total <= itemsPerPage} />
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  manageLicenses: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    height: '100%',
    background: '#FFF',
    borderRadius: '12px',
    padding: '28px 32px',
    [theme.breakpoints.down('lg')]: {
      padding: '24px',
    },
  },
  listHeader: {
    flexWrap: 'wrap',
  },
  blockTitle: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 500,
  },
  licenses: {
    overflow: 'auto',
  },
  licenseItem: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greys.lighter}`,
  },
  licenseItemHeader: {
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  licenseItemContent: {
    padding: '16px',
  },
  logo: {
    width: '28px',
    height: '28px',
  },
  licenseName: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 500,
  },
  licenseUserCount: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  iconButton: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.greys.medium,
    },
    '&.delete': {
      '& .MuiSvgIcon-root': {
        color: theme.palette.error.main,
      },
    },
  },
  primaryText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.primary,
  },
  secondaryText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.text.primary,
  },
  statusIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.text.primary,
    '&.expired': {
      color: theme.palette.error.main,
    },
    '&.canceled': {
      color: theme.palette.warning.main,
    },
    '&.active': {
      color: theme.palette.success.main,
    },
  },
}));

export default ManageLicenses;
